/**
 * 参謀くん連携ボタンの Disable 理由を表す列挙。
 * UI はこの値をツールチップ文言へ写像する。
 */
export type SamboKunSendDisableReason =
  | "ok"
  | "limit-loading"
  | "limit-fetch-failed"
  | "unknown-count"
  | "exceed-limit";

/**
 * 連携ボタンを Disable にする理由を返す。
 *
 * 判定順:
 *  1. リミット API エラー → "limit-fetch-failed"
 *  2. リミット未取得（初回ロード中 / リトライ中）→ "limit-loading"
 *     （loading とエラーで文言を分け、誤った「取得失敗」表示を防ぐ）
 *  3. 物件数が未取得（undefined）→ "unknown-count"
 *     （API 契約バリデーションが物件数を必須化していない場合の保険）
 *  4. 物件数 > 上限 → "exceed-limit"
 *  5. それ以外 → "ok"
 *
 * @param propertyCount - 物件数。詳細画面の応答に欠落している場合は undefined。
 * @param maxPropertiesPerRequest - 上限件数。リミット API 未取得時は null。
 * @param isLimitError - リミット API がエラー状態か。
 */
export const getSamboKunSendDisableReason = (
  propertyCount: number | undefined,
  maxPropertiesPerRequest: number | null,
  isLimitError: boolean
): SamboKunSendDisableReason => {
  if (isLimitError) {
    return "limit-fetch-failed";
  }
  if (maxPropertiesPerRequest == null) {
    return "limit-loading";
  }
  if (propertyCount == null) {
    return "unknown-count";
  }
  if (propertyCount > maxPropertiesPerRequest) {
    return "exceed-limit";
  }
  return "ok";
};

/**
 * 連携ボタンを Disable すべきかを返す。
 *
 * @see getSamboKunSendDisableReason
 */
export const isSendToSamboKunDisabled = (
  propertyCount: number | undefined,
  maxPropertiesPerRequest: number | null,
  isLimitError: boolean
): boolean => {
  return (
    getSamboKunSendDisableReason(
      propertyCount,
      maxPropertiesPerRequest,
      isLimitError
    ) !== "ok"
  );
};

/**
 * Disable 理由 → ツールチップ文言。
 * "ok" は Disable ではないので含めない。
 *
 * "limit-loading" は短時間で解消する想定のため、誤情報を表示せず空文字に
 * マップ（ツールチップ非表示）。ボタンは Disable のままだが文言が出ない。
 */
export const samboKunSendDisabledTooltip: Record<
  Exclude<SamboKunSendDisableReason, "ok">,
  string
> = {
  "limit-loading": "",
  "limit-fetch-failed":
    "連携上限件数を取得できませんでした。しばらく待ってから再試行してください",
  "unknown-count":
    "物件数を取得できませんでした。しばらく待ってから再試行してください",
  "exceed-limit": "連携可能な物件数の上限を超えています",
};
